import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { AppShell } from "@/components/AppShell";
import { DemoBadge, ConfidenceBadge } from "@/components/Badges";
import {
  Stethoscope, Pill, Accessibility as AccessIcon, CloudSun, Siren,
  ClipboardList, Download, RefreshCw, MapPin, ExternalLink, AlertTriangle, WifiOff,
} from "lucide-react";
import api from "@/lib/api";
import { cacheReport, readReport } from "@/lib/offlineCache";

const STATUS = [
  "Checking healthcare access…",
  "Mapping pharmacies…",
  "Reviewing accessibility…",
  "Preparing your report…",
];

const SECTION_KEYS = ["overview", "healthcare", "pharmacies", "accessibility", "environment", "emergency", "checklist"];

function mapsLink(query) {
  return `https://www.google.com/maps/search/${encodeURIComponent(query)}`;
}

export default function Report() {
  const { tripId } = useParams();
  const contentRef = useRef(null);
  const tripRef = useRef(null);
  const [trip, setTrip] = useState(null);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [statusIdx, setStatusIdx] = useState(0);
  const [checked, setChecked] = useState({});
  const [openSections, setOpenSections] = useState(["overview", "healthcare"]);
  const [error, setError] = useState(false);
  const [offlineAt, setOfflineAt] = useState(null);

  useEffect(() => { tripRef.current = trip; }, [trip]);

  const generate = useCallback(async () => {
    setError(false);
    setGenerating(true);
    try {
      const { data } = await api.post(`/trips/${tripId}/report`, null, { timeout: 0 });
      setReport(data);
      setChecked(data.checklist_state || {});
      setOfflineAt(null);
      cacheReport(tripId, tripRef.current, data);
    } catch {
      // The edge may time out (~60s) while the backend still saves the report — poll GET.
      let ok = false;
      for (let i = 0; i < 8 && !ok; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const r = await api.get(`/trips/${tripId}/report`);
          setReport(r.data);
          setChecked(r.data.checklist_state || {});
          setOfflineAt(null);
          cacheReport(tripId, tripRef.current, r.data);
          ok = true;
        } catch {
          /* keep polling */
        }
      }
      if (!ok) setError(true);
    } finally {
      setGenerating(false);
    }
  }, [tripId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let tripData = null;
      try {
        const t = await api.get(`/trips/${tripId}`);
        tripData = t.data;
        if (!cancelled) setTrip(tripData);
      } catch (err) {
        // Offline and can't load the trip — fall back to the cached report bundle.
        if (!err?.response) {
          const c = readReport(tripId);
          if (c && c.report && !cancelled) {
            setTrip(c.trip);
            setReport(c.report);
            setChecked(c.report.checklist_state || {});
            setOfflineAt(c.savedAt);
            return;
          }
        }
      }
      try {
        const r = await api.get(`/trips/${tripId}/report`);
        if (cancelled) return;
        setReport(r.data);
        setChecked(r.data.checklist_state || {});
        setOfflineAt(null);
        cacheReport(tripId, tripData, r.data);
      } catch (err) {
        const c = readReport(tripId);
        if (!err?.response && c && c.report) {
          if (cancelled) return;
          setTrip(c.trip);
          setReport(c.report);
          setChecked(c.report.checklist_state || {});
          setOfflineAt(c.savedAt);
        } else {
          generate();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tripId, generate]);

  useEffect(() => {
    if (!generating) return;
    const id = setInterval(() => setStatusIdx((i) => (i + 1) % STATUS.length), 1400);
    return () => clearInterval(id);
  }, [generating]);

  const toggleCheck = async (idx) => {
    const next = { ...checked, [idx]: !checked[idx] };
    setChecked(next);
    try {
      await api.patch(`/reports/${report.id}/checklist`, { checked: next });
    } catch {
      /* keep local state */
    }
  };

  const downloadPdf = () => {
    setOpenSections(SECTION_KEYS);
    setTimeout(() => window.print(), 300);
  };

  if (error && !report) {
    return (
      <AppShell active="trips" title="Your report" showBack>
        <div data-testid="report-error" className="mt-6 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-6 text-center">
          <AlertTriangle className="mx-auto text-amber-500" size={36} />
          <h2 className="font-head text-xl font-bold text-[#F8FAFC] mt-3">This is taking longer than expected</h2>
          <p className="text-[#A1A1AA] mt-2">
            Your report may still be finishing. Try again in a moment.
          </p>
          <Button
            data-testid="report-retry-btn"
            onClick={generate}
            className="mt-5 w-full min-h-[56px] rounded-xl bg-[#EF9F27] hover:bg-[#EF9F27] text-white text-lg font-semibold"
          >
            <RefreshCw size={20} className="mr-2" /> Try again
          </Button>
        </div>
      </AppShell>
    );
  }

  if (generating || !report) {
    return (
      <AppShell active="trips" title="Your report" showBack>
        <div data-testid="report-loading" className="space-y-5">
          <div className="flex items-center gap-3 bg-[#EF9F27]/15 rounded-2xl p-4">
            <RefreshCw className="animate-spin text-[#EF9F27]" />
            <p className="font-semibold text-[#F8FAFC]" data-testid="report-status-text">
              {STATUS[statusIdx]}
            </p>
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-[#1A1A1A] rounded-2xl border border-[#333333] p-5 space-y-3">
              <div className="h-5 w-1/3 bg-[#333333] rounded animate-pulse" />
              <div className="h-4 w-full bg-[#222222] rounded animate-pulse" />
              <div className="h-4 w-5/6 bg-[#222222] rounded animate-pulse" />
            </div>
          ))}
        </div>
      </AppShell>
    );
  }

  const r = report.report_json;
  const isDemo = report.is_demo_data;

  return (
    <AppShell active="trips" title="Confidence Report" showBack contentRef={contentRef}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold text-[#F8FAFC]">
            {trip?.destination_city}, {trip?.destination_country}
          </h1>
          <p className="text-[#A1A1AA] text-sm">
            {trip?.start_date} → {trip?.end_date}
          </p>
        </div>
        {isDemo && <DemoBadge className="mt-1" />}
      </div>

      {isDemo && (
        <div className="mt-3 bg-[#1A1A1A] border border-[#333333] rounded-xl p-3 text-sm text-[#A1A1AA] flex gap-2">
          <AlertTriangle size={18} className="text-[#666666] shrink-0 mt-0.5" />
          This report uses demo data. Connect an Anthropic API key to generate live guidance.
        </div>
      )}

      {offlineAt && (
        <div data-testid="report-offline-banner" className="mt-3 flex items-center gap-2 text-sm text-[#EFC77A] bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-xl px-3 py-2">
          <WifiOff size={16} className="shrink-0" />
          <span>Offline — showing your saved report from {new Date(offlineAt).toLocaleDateString()}.</span>
        </div>
      )}

      <Accordion
        type="multiple"
        value={openSections}
        onValueChange={setOpenSections}
        className="mt-5 space-y-3"
      >
        {/* Overview */}
        <AccordionItem value="overview" className="bg-[#1A1A1A] border border-[#333333] rounded-2xl px-4">
          <AccordionTrigger data-testid="section-overview" className="min-h-[56px] hover:no-underline">
            <span className="font-head text-lg font-bold text-[#F8FAFC]">Overview</span>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <div className="mb-3">
              <ConfidenceBadge level={r.overview?.confidence_level} />
            </div>
            <p className="text-[#F8FAFC] leading-relaxed">{r.overview?.summary}</p>
          </AccordionContent>
        </AccordionItem>

        {/* Healthcare */}
        <AccordionItem value="healthcare" className="bg-[#1A1A1A] border border-[#333333] rounded-2xl px-4">
          <AccordionTrigger data-testid="section-healthcare" className="min-h-[56px] hover:no-underline">
            <span className="flex items-center gap-2 font-head text-lg font-bold text-[#F8FAFC]">
              <Stethoscope size={20} className="text-[#EF9F27]" /> Healthcare Access
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 space-y-3">
            <p className="text-[#F8FAFC] leading-relaxed">{r.healthcare_access?.notes}</p>
            {(r.healthcare_access?.facilities || []).map((f, i) => (
              <div key={i} className="border border-[#333333] rounded-xl overflow-hidden">
                <div className="h-20 bg-gradient-to-br from-[#1A1A1A] to-[#EF9F27] flex items-center justify-center">
                  <MapPin className="text-white/90" />
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-[#F8FAFC]">{f.name}</h4>
                    <span className="text-sm text-[#A1A1AA]">{f.distance_km} km</span>
                  </div>
                  <p className="text-sm text-[#A1A1AA]">{f.type}</p>
                  {f.relevant_because && (
                    <p className="text-sm text-[#EF9F27] mt-1">{f.relevant_because}</p>
                  )}
                  <a
                    href={mapsLink(`${f.name} ${trip?.destination_city}`)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#EF9F27]"
                  >
                    Open in Maps <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* Pharmacies */}
        <AccordionItem value="pharmacies" className="bg-[#1A1A1A] border border-[#333333] rounded-2xl px-4">
          <AccordionTrigger data-testid="section-pharmacies" className="min-h-[56px] hover:no-underline">
            <span className="flex items-center gap-2 font-head text-lg font-bold text-[#F8FAFC]">
              <Pill size={20} className="text-[#EF9F27]" /> Pharmacies & Medications
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 space-y-3">
            <p className="text-[#F8FAFC] leading-relaxed">{r.pharmacies_medications?.notes}</p>
            <ul className="space-y-2">
              {(r.pharmacies_medications?.pharmacies || []).map((p, i) => (
                <li key={i} className="flex items-center justify-between border border-[#333333] rounded-xl p-3">
                  <div>
                    <p className="font-semibold text-[#F8FAFC]">{p.name}</p>
                    <p className="text-sm text-[#A1A1AA]">{p.hours}</p>
                  </div>
                  <span className="text-sm text-[#A1A1AA]">{p.distance_km} km</span>
                </li>
              ))}
            </ul>
            <div className="bg-[#111111] rounded-xl p-3">
              <p className="text-sm font-semibold text-[#F8FAFC] mb-1">Medication notes</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-[#F8FAFC]">
                {(r.pharmacies_medications?.medication_notes || []).map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Accessibility */}
        <AccordionItem value="accessibility" className="bg-[#1A1A1A] border border-[#333333] rounded-2xl px-4">
          <AccordionTrigger data-testid="section-accessibility" className="min-h-[56px] hover:no-underline">
            <span className="flex items-center gap-2 font-head text-lg font-bold text-[#F8FAFC]">
              <AccessIcon size={20} className="text-[#EF9F27]" /> Accessibility Snapshot
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 space-y-3 text-[#F8FAFC]">
            <div><p className="font-semibold text-[#F8FAFC]">Transport</p><p>{r.accessibility?.transport}</p></div>
            <div><p className="font-semibold text-[#F8FAFC]">Accommodation</p><p>{r.accessibility?.accommodation}</p></div>
            <div><p className="font-semibold text-[#F8FAFC]">Venues</p><p>{r.accessibility?.venues}</p></div>
          </AccordionContent>
        </AccordionItem>

        {/* Environment */}
        <AccordionItem value="environment" className="bg-[#1A1A1A] border border-[#333333] rounded-2xl px-4">
          <AccordionTrigger data-testid="section-environment" className="min-h-[56px] hover:no-underline">
            <span className="flex items-center gap-2 font-head text-lg font-bold text-[#F8FAFC]">
              <CloudSun size={20} className="text-[#EF9F27]" /> Environment
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 space-y-2 text-[#F8FAFC]">
            <p><span className="font-semibold text-[#F8FAFC]">Climate:</span> {r.environment?.climate}</p>
            <p><span className="font-semibold text-[#F8FAFC]">Altitude:</span> {r.environment?.altitude}</p>
            <p><span className="font-semibold text-[#F8FAFC]">Air quality:</span> {r.environment?.air_quality}</p>
            {r.environment?.condition_relevance && (
              <p className="text-[#EF9F27]">{r.environment.condition_relevance}</p>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Emergency prep */}
        <AccordionItem value="emergency" className="bg-[#1A1A1A] border border-[#333333] rounded-2xl px-4">
          <AccordionTrigger data-testid="section-emergency" className="min-h-[56px] hover:no-underline">
            <span className="flex items-center gap-2 font-head text-lg font-bold text-[#F8FAFC]">
              <Siren size={20} className="text-[#D32F2F]" /> Emergency Preparation
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 space-y-2 text-[#F8FAFC]">
            <p><span className="font-semibold text-[#F8FAFC]">Emergency number:</span> {r.emergency_prep?.emergency_number}</p>
            <p><span className="font-semibold text-[#F8FAFC]">Embassy:</span> {r.emergency_prep?.embassy}</p>
            <p><span className="font-semibold text-[#F8FAFC]">Nearest appropriate facility:</span> {r.emergency_prep?.nearest_appropriate_facility}</p>
            {(r.emergency_prep?.phrases || []).length > 0 && (
              <div className="bg-[#111111] rounded-xl p-3 mt-2">
                <p className="text-sm font-semibold text-[#F8FAFC] mb-1">Useful phrases</p>
                {r.emergency_prep.phrases.map((p, i) => (
                  <p key={i} className="text-sm">
                    <span className="text-[#A1A1AA]">{p.english}</span> — <span className="font-medium">{p.local}</span>
                  </p>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Checklist */}
        <AccordionItem value="checklist" className="bg-[#1A1A1A] border border-[#333333] rounded-2xl px-4">
          <AccordionTrigger data-testid="section-checklist" className="min-h-[56px] hover:no-underline">
            <span className="flex items-center gap-2 font-head text-lg font-bold text-[#F8FAFC]">
              <ClipboardList size={20} className="text-[#EF9F27]" /> Pre-Travel Checklist
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 space-y-2">
            {(r.checklist || []).map((c, i) => (
              <label
                key={i}
                data-testid={`checklist-item-${i}`}
                className="flex gap-3 items-start border border-[#333333] rounded-xl p-3 cursor-pointer"
              >
                <Checkbox
                  checked={!!checked[i]}
                  onCheckedChange={() => toggleCheck(i)}
                  className="mt-0.5 h-6 w-6 data-[state=checked]:bg-[#EF9F27] data-[state=checked]:border-[#EF9F27]"
                  data-testid={`checklist-checkbox-${i}`}
                />
                <div>
                  <p className={`font-medium ${checked[i] ? "line-through text-[#666666]" : "text-[#F8FAFC]"}`}>
                    {c.item}
                  </p>
                  <p className="text-sm text-[#A1A1AA]">{c.why}</p>
                </div>
              </label>
            ))}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Always-visible disclaimer */}
      <div
        data-testid="report-disclaimer"
        role="note"
        className="mt-4 bg-[#1A1A1A] text-white rounded-2xl p-4"
      >
        <p className="text-sm leading-relaxed">{r.disclaimer}</p>
      </div>

      <div className={`mt-5 grid ${offlineAt ? "grid-cols-1" : "grid-cols-2"} gap-3 no-print`}>
        <Button
          data-testid="download-pdf-btn"
          onClick={downloadPdf}
          variant="outline"
          className="min-h-[56px] rounded-xl border-2 border-[#1A1A1A] text-[#F8FAFC] font-semibold"
        >
          <Download size={20} className="mr-2" /> Download PDF
        </Button>
        {!offlineAt && (
          <Button
            data-testid="regenerate-btn"
            onClick={generate}
            className="min-h-[56px] rounded-xl bg-[#EF9F27] hover:bg-[#EF9F27] text-white font-semibold"
          >
            <RefreshCw size={20} className="mr-2" /> Regenerate
          </Button>
        )}
      </div>
    </AppShell>
  );
}
